import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import WorkspacePage from './WorkspacePage'
import '../../pages/CreateRoom/createroom.scss'
import { WorkspaceContext } from './WorkspaceContext'
import type { WorkspaceCtx } from './WorkspaceContext'
// import { Snippet } from './snippets/SnippetContainer'
import axios from 'axios'
import { useParams } from 'react-router-dom'
import type { Snippet } from './snippets/SnippetContainer'
import type { FileRef } from './WorkspaceContext'
import type { RoomMemberDetails } from './WorkspaceContext'
import type { roomDetails } from './WorkspaceContext'
import { socket } from '../../Socket';
import { useJoinRoom } from '../../Socket'
import type { Member } from './WorkspaceContext'
import type { YourRole } from './WorkspaceContext'
import { useNavigate } from 'react-router-dom'
import type { Folder } from './sidebar/SideBarTypes'
import type { ActiveResourceId, OpenTab } from './WorkspaceContext'
import type { MainUiMode, SBmodeTabs } from './WorkspaceContext'
import api from '../../api'
import { readJSONCookie, writeJSONCookie, getCookie } from '../../shared/utils/cookies'
import { getOrCreateClientId } from '../../context/clientIdHelper'
import { useAuth } from 'react-oidc-context'
import { patchFolderInTree, insertFolderNode, addIdToFolderArray, removeIdFromFolderArrays, extractFolderNode, removeFolderIds } from '../../shared/utils/folderTree'
import { containsProfanities } from '../../shared/utils/ContainsProfanity'
import { CodeRunnerContext } from '../coderunner/CodeRunnerContext'
import type { CodeRunnerCtx } from '../coderunner/CodeRunnerContext'

const API = process.env.REACT_APP_API_URL;
const PRESENCE_HEARTBEAT_MS = 120_000;
const ACCOUNT_TOKEN_REFRESH_SKEW_SECONDS = 60;
const WAKE_RECOVERY_AFTER_MS = 10 * 60 * 1000;
const PLACEHOLDER_NAMES = new Set(['flashroom user', 'owner']);

function hasUsableLocalName(name?: string | null) {
    const normalized = name?.trim().toLowerCase();
    return !!normalized && !PLACEHOLDER_NAMES.has(normalized);
}

function shouldRefreshAuthUser(authUser?: ReturnType<typeof useAuth>['user']) {
    if (!authUser?.access_token) return false;
    if (authUser.expired) return true;
    if (!authUser.expires_at) return false;

    return authUser.expires_at - Math.floor(Date.now() / 1000) <= ACCOUNT_TOKEN_REFRESH_SKEW_SECONDS;
}

const WorkspaceProvider = () => {
    const { roomId } = useParams();
    const navigate = useNavigate();
    const auth = useAuth();
    const [accessGranted, setAccessGranted] = useState(false);
    const verifiedRoomRef = useRef<string | null>(null);
    const [shareableNamePrompt, setShareableNamePrompt] = useState(false);
    const [shareableJoinName, setShareableJoinName] = useState('');
    const [shareableJoinError, setShareableJoinError] = useState('');
    const [shareableJoinLoading, setShareableJoinLoading] = useState(false);

    useEffect(() => {
        if (verifiedRoomRef.current !== roomId) {
            verifiedRoomRef.current = roomId ?? null;
            setAccessGranted(false);
        }

        if (auth.isLoading) {
            if (!accessGranted) setAccessGranted(false);
            return;
        }

        if (auth.activeNavigator === 'signinSilent') {
            return;
        }

        if (!roomId) {
            navigate("/join", { replace: true });
            return;
        }

        let cancelled = false;

        const completeShareableLocalJoin = async (name: string) => {
            if (!roomId) return false;
            const clientId = getOrCreateClientId();
            const { data } = await axios.post(`${API}/api/rooms/shareable/${roomId}/join`, {
                member: {
                    clientId,
                    name,
                },
            });

            if (data?.token) {
                localStorage.setItem("flashroom_roomToken", data.token);
            }

            localStorage.setItem("flashroom_most_recent", roomId);
            return true;
        };

        const tryShareableJoin = async (accountAccessToken?: string | null) => {
            if (!roomId) return false;

            const { data: access } = await axios.get(`${API}/api/rooms/shareable/${roomId}/access`);

            if (!access?.shareableLinkEnabled) {
                return false;
            }

            if (accountAccessToken) {
                const { data } = await api.post(
                    `/rooms/account/${roomId}/shareable-join`,
                    undefined,
                    { headers: { Authorization: `Bearer ${accountAccessToken}` } }
                );

                if (data?.token) {
                    localStorage.setItem("flashroom_roomToken", data.token);
                }

                localStorage.setItem("flashroom_most_recent", roomId);
                return true;
            }

            const savedName = localStorage.getItem("flashroom-accountName")?.trim();

            if (access.trackMembersEnabled && !hasUsableLocalName(savedName)) {
                setShareableNamePrompt(true);
                return "prompt" as const;
            }

            await completeShareableLocalJoin(access.trackMembersEnabled ? savedName || "flashroom user" : "flashroom user");
            return true;
        };

        const verifyRoomAccess = async () => {
            try {
                const restoredUser =
                    !auth.isAuthenticated && auth.user?.refresh_token
                        ? await auth.signinSilent()
                        : null;
                const accountAccessToken =
                    auth.isAuthenticated && auth.user?.access_token
                        ? auth.user.access_token
                        : restoredUser?.access_token;

                const data = accountAccessToken
                    ? (
                        await api.patch(
                            `/rooms/account/${roomId}/rejoin`,
                            undefined,
                            { headers: { Authorization: `Bearer ${accountAccessToken}` } }
                        )
                    ).data
                    : (
                        await (async () => {
                            const clientId = getOrCreateClientId();
                            return axios.patch(`${API}/api/rooms/${roomId}/members/${clientId}/rejoin`);
                        })()
                    ).data;

                if (cancelled) return;

                if (data?.token) {
                    localStorage.setItem("flashroom_roomToken", data.token);
                }

                localStorage.setItem("flashroom_most_recent", roomId);
                setAccessGranted(true);
            } catch (error: any) {
                if (cancelled) return;

                const code = error?.response?.data?.code;
                if (code === "MEMBER_NOT_FOUND") {
                    try {
                        const shareableResult = await tryShareableJoin(
                            auth.isAuthenticated && auth.user?.access_token
                                ? auth.user.access_token
                                : null
                        );

                        if (cancelled) return;

                        if (shareableResult === true) {
                            setAccessGranted(true);
                            return;
                        }

                        if (shareableResult === "prompt") {
                            return;
                        }
                    } catch (shareableError: any) {
                        if (shareableError?.response?.status === 403 && shareableError?.response?.data?.code === "BANNED") {
                            navigate("/kicked", { replace: true });
                            return;
                        }

                        if (shareableError?.response?.data?.code === "ROOM_FULL") {
                            navigate("/room-full", { replace: true });
                            return;
                        }
                    }
                }

                navigate(code === "ROOM_NOT_FOUND" ? "/deleted" : "/join", { replace: true });
            }
        };

        if (!accessGranted) setAccessGranted(false);
        verifyRoomAccess();

        return () => {
            cancelled = true;
        };
    }, [roomId, navigate, auth.isLoading, auth.isAuthenticated, auth.activeNavigator, auth.user?.access_token, auth.user?.refresh_token, accessGranted]);

    const submitShareableName = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!roomId || shareableJoinLoading) return;

        const name = shareableJoinName.trim();
        if (!hasUsableLocalName(name)) {
            setShareableJoinError('Enter your name to join this room.');
            return;
        }

        if (containsProfanities(name)) {
            setShareableJoinError('Choose a different display name.');
            return;
        }

        try {
            setShareableJoinLoading(true);
            setShareableJoinError('');

            const clientId = getOrCreateClientId();
            const { data } = await axios.post(`${API}/api/users`, { name, clientId });
            const id = data?.user?.id;

            localStorage.setItem("flashroom-accountName", name);
            if (id) localStorage.setItem("flashroom-accountId", id);

            const { data: room } = await axios.post(`${API}/api/rooms/shareable/${roomId}/join`, {
                member: {
                    clientId,
                    name,
                },
            });

            if (room?.token) {
                localStorage.setItem("flashroom_roomToken", room.token);
            }

            localStorage.setItem("flashroom_most_recent", roomId);
            setShareableNamePrompt(false);
            setAccessGranted(true);
        } catch (error: any) {
            if (error?.response?.status === 403 && error?.response?.data?.code === "BANNED") {
                navigate("/kicked", { replace: true });
                return;
            }

            if (error?.response?.data?.code === "ROOM_FULL") {
                navigate("/room-full", { replace: true });
                return;
            }

            setShareableJoinError('Unable to join this room. Please try again.');
        } finally {
            setShareableJoinLoading(false);
        }
    };

    if (shareableNamePrompt) {
        return (
            <div id="create-room" className="shareable-name-page" style={{ backgroundImage: `url('/images/cbb2.png')` }}>
                <div id="name-prompt" className="shareable-name-card">
                    <h3>What is your name?</h3>
                    <p>Enter a display name to join this room.</p>
                    <form id="nameform" onSubmit={submitShareableName}>
                        <input
                            maxLength={20}
                            type="text"
                            value={shareableJoinName}
                            onChange={(e) => setShareableJoinName(e.target.value)}
                            placeholder="enter your name"
                        />
                        {shareableJoinError && <p className="join-error" role="alert">{shareableJoinError}</p>}
                        <button type="submit" disabled={shareableJoinLoading}>
                            {shareableJoinLoading ? 'Joining...' : 'Join Room'}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    if (!accessGranted) return null;

    return <WorkspaceRoom />;
};



const WorkspaceRoom = () => {

    const [ctheme, setcTheme] = useState("dark");

    const [roomDetails, setRoomDetails] = useState<roomDetails | null>(null);
    const [memberDetails, setMemberDetails] = useState<RoomMemberDetails | null>(null);
    const [prompt, setPrompt] = useState(false);
    const [activeCode, setActiveCode] = useState('');
    const [activeLanguage, setActiveLanguage] = useState('');
    const [activeTerminal, setActiveTerminal] = useState(false);
    const [activeWebPreview, setActiveWebPreview] = useState(false);
    const [previewDoc, setPreviewDoc] = useState('');
    const [sideBarOpen, toggleSideBarOpen] = useState(true);
    const outputRef = useRef<string>('');
    const termWriteRef = useRef<((s: string) => void) | null>(null);
    const termReadLineRef = useRef<(() => Promise<string>) | null>(null);
    const jsRunRef = useRef<((code: string) => void) | null>(null); // <--
    const [openSnippets, setOpenSnippets] = useState<Snippet[]>([]);
    const [activeSnippetId, setActiveSnippetId] = useState<Snippet['_id'] | null>(null);
    const [activeFileId, setActiveFileId] = useState<ActiveResourceId>(null);
    const [Snippets, setSnippets] = useState<Snippet[]>([]);
    const [Folders, setFolders] = useState<Folder[]>([]);
    const [Files, setFiles] = useState<FileRef[]>([]);
    const [newSnipPopup, setNewSnipPopup] = useState(false);
    const [membersList, setMembersList] = useState<Member[]>([]);
    const [clientId, setClientId] = useState<string | null>(null);
    const [pendingOpenId, setPendingOpenId] = useState<string | null>(null)
    const [roomStorage, setRoomStorage] = useState<number>(0);
    const [globalNewFolderPopup, setGlobalNewFolderPopup] = useState(false);
    const [addNewPopup, setAddNewPopup] = useState(false);
    const [downloadFileErrorPopup, setDownloadFileErrorPopup] = useState(false);
    const [roomSettingsInfoPopup, setRoomSettingsInfoPopup] = useState(false);
    const [qrPopup, setQrPopup] = useState(false);
    const [uiMode, setUiMode] = useState<MainUiMode>(() => {
        const savedUiMode = localStorage.getItem("flashroom-ui-mode");
        return savedUiMode === "files" ? "files" : "editor";
    });
    const [sbMode, setSbMode] = useState<SBmodeTabs>("editor");


    const [limitPopup, setLimitPopup] = useState(false);
    const [limitMessage, setLimitMessage] = useState<string | null>(null);
    const [yourRole, setYourRole] = useState<YourRole | null>(null);


    const [openTabs, setOpenTabs] = useState<OpenTab[]>([]);
    const hydratedRoomIdsRef = useRef<Set<string>>(new Set());

    // Granular patch helpers — apply a single resource's changed fields directly to
    // local state instead of waiting on the paired "X:refresh" full-collection reload.
    const applyStorageDelta = useCallback((delta?: number) => {
        if (!delta) return;
        setRoomStorage(prev => prev + delta);
    }, []);

    const patchSnippetById = useCallback((id: string, fields: Partial<Snippet>, updatedAt?: string) => {
        setSnippets(prev => {
            let changed = false;

            const next = prev.map(s => {
                if (s._id !== id) return s;

                if (updatedAt && s.updatedAt && new Date(updatedAt).getTime() < new Date(s.updatedAt).getTime()) {
                    return s;
                }

                changed = true;
                return { ...s, ...fields, ...(updatedAt ? { updatedAt } : {}) };
            });

            return changed ? next : prev;
        });
    }, []);

    const patchFolderById = useCallback((id: string, fields: Partial<Folder>) => {
        setFolders(prev => patchFolderInTree(prev, id, fields));
    }, []);

    const patchFileById = useCallback((id: string, fields: Partial<FileRef>) => {
        setFiles(prev => {
            let changed = false;

            const next = prev.map(f => {
                if (f._id !== id) return f;
                changed = true;
                return { ...f, ...fields };
            });

            return changed ? next : prev;
        });
    }, []);

    const removeSnippetById = useCallback((id: string) => {
        setSnippets(prev => prev.filter(s => s._id !== id));
        setFolders(prev => removeIdFromFolderArrays(prev, "snippets", id));
    }, []);

    const removeFileById = useCallback((id: string) => {
        setFiles(prev => prev.filter(f => f._id !== id));
        setFolders(prev => removeIdFromFolderArrays(prev, "fileRefs", id));
    }, []);

    const patchMemberById = useCallback((clientId: string, fields: Partial<Member>) => {
        setMembersList(prev => {
            let changed = false;

            const next = prev.map(m => {
                if (m.clientId !== clientId) return m;
                changed = true;
                return { ...m, ...fields };
            });

            return changed ? next : prev;
        });
    }, []);

    const activeMembers = useMemo(
        () => membersList.filter(m => m.isActive),
        [membersList]
    );

    const roomSettings = useMemo(() => ({
        shareableLinkEnabled: roomDetails?.shareableLinkEnabled ?? false,
        trackMembersEnabled: roomDetails?.trackMembersEnabled ?? true,
        maxMembersEnabled: roomDetails?.maxMembersEnabled ?? false,
        maxMembers: roomDetails?.maxMembers ?? null,
        defaultMemberRole: roomDetails?.defaultMemberRole ?? 'member',
    }), [roomDetails]);

    const [globalNewFilePopup, setGlobalNewFilePopup] = useState(false);
    const [globalNewSnipPopup, setGlobalNewSnipPopup] = useState(false);

    // const activeSnippet = useMemo(
    //     () => openSnippets.find(s => s._id === activeSnippetId) ?? null,
    //     [openSnippets, activeSnippetId]
    // );

    const activeSnippet = useMemo(() => {
        if (!activeFileId || activeFileId.resourceType !== "snippet") return null;

        const activeTab = openTabs.find(
            tab =>
                tab.resourceType === "snippet" &&
                tab.data._id === activeFileId.id
        );

        return activeTab?.resourceType === "snippet" ? activeTab.data : null;
    }, [openTabs, activeFileId]);

    type ActiveFile =
        | { resourceType: "snippet"; data: Snippet }
        | { resourceType: "file"; data: FileRef }
        | null;

    const activeFile = useMemo<ActiveFile>(() => {
        if (!activeFileId) return null;

        const activeTab = openTabs.find(
            tab =>
                tab.resourceType === activeFileId.resourceType &&
                tab.data._id === activeFileId.id
        );

        if (!activeTab) return null;

        if (activeTab.resourceType === "snippet") {
            return {
                resourceType: "snippet",
                data: activeTab.data,
            };
        }

        return {
            resourceType: "file",
            data: activeTab.data,
        };
    }, [openTabs, activeFileId]);



    const { roomId } = useParams();
    const auth = useAuth();
    useJoinRoom(roomId);


    const addNewSnippetToOpen = (id: Snippet["_id"]) => {
        const sid = String(id);
        const snip = Snippets.find(s => String(s._id) === sid);
        if (!snip) return false;

        // Only auto-open/auto-activate the newly created file when the user is
        // actually looking at the editor. Doing this while browsing Drive made
        // whatever file was just created show up highlighted as "active" in the
        // Drive grid even though the user never opened it there.
        if (uiMode === 'editor') {
            setOpenSnippets(prev =>
                prev.some(s => String(s._id) === sid) ? prev : [snip, ...prev]
            );
            setOpenTabs(prev => [{ resourceType: 'snippet', data: snip }, ...prev])

            setActiveSnippetId(snip._id);
            setActiveFileId({ resourceType: 'snippet', id: id });

            const ids = readJSONCookie<string[]>("openSnippets", []);
            if (!ids.includes(sid)) {
                ids.unshift(sid);
                if (ids.length > 20) ids.length = 20;
                writeJSONCookie("openSnippets", ids, { days: 30, path: "/", sameSite: "Lax" });
            }
        }

        return true;
    };



    useEffect(() => {
        const id = localStorage.getItem('flashroom_id');
        setClientId(id);
    }, [roomId]);

    useEffect(() => {
        if (!pendingOpenId) return;
        if (addNewSnippetToOpen(pendingOpenId)) setPendingOpenId(null);

    }, [pendingOpenId, Snippets]);

    // Persist/restore which files & snippets are open, per room. Lives here
    // (not in SnippetContainer) specifically because WorkspaceRoom's identity
    // *is* one room visit — it mounts once on room entry and unmounts on
    // Home<->Room navigation, but does NOT unmount when uiMode toggles
    // Editor<->Drive. SnippetContainer, by contrast, unmounts every time the
    // last open tab closes (that switches uiMode to "files"/Drive) — so an
    // effect living there watching `openTabs` never gets to run with the
    // final empty value before the component disappears, leaving the cookie
    // stale. Anchoring both effects up here avoids that race entirely.
    type SavedOpenTab = {
        resourceType: "snippet" | "file";
        id: string;
    };

    const openTabsCookieKeyFor = (roomId: string) => `openTabs::${roomId}`;

    useEffect(() => {
        if (!roomId) return;
        if (hydratedRoomIdsRef.current.has(roomId)) return;

        try {
            const key = openTabsCookieKeyFor(roomId);
            // Distinguish "never saved" (fresh room -> auto-open the first few
            // files) from "saved as explicitly empty" (user closed every open
            // file last visit and was placed into Drive mode -> stay empty and
            // respect that) — readJSONCookie's fallback collapses both to [].
            const cookieWasSet = getCookie(key) !== null;
            const savedTabs = readJSONCookie<SavedOpenTab[]>(key, []);

            const needsSnippets = savedTabs.some(tab => tab.resourceType === "snippet");
            const needsFiles = savedTabs.some(tab => tab.resourceType === "file");

            if (needsSnippets && Snippets.length === 0) {
                return;
            }

            if (needsFiles && Files.length === 0) {
                return;
            }

            if (!cookieWasSet && Snippets.length === 0 && Files.length === 0) {
                return;
            }

            const snippetMap = new Map(Snippets.map(snippet => [snippet._id, snippet]));
            const fileMap = new Map(Files.map(file => [file._id, file]));

            const restoredTabs: OpenTab[] = savedTabs
                .map(tab => {
                    if (tab.resourceType === "snippet") {
                        const snippet = snippetMap.get(tab.id);

                        return snippet
                            ? {
                                resourceType: "snippet" as const,
                                data: snippet,
                            }
                            : null;
                    }

                    const file = fileMap.get(tab.id);

                    return file
                        ? {
                            resourceType: "file" as const,
                            data: file,
                        }
                        : null;
                })
                .filter((tab): tab is OpenTab => tab !== null);

            const fallbackTabs: OpenTab[] =
                restoredTabs.length > 0 || cookieWasSet
                    ? restoredTabs
                    : Snippets.length > 0
                        ? Snippets.slice(0, Math.min(5, Snippets.length)).map(snippet => ({
                            resourceType: "snippet" as const,
                            data: snippet,
                        }))
                        : Files.slice(0, Math.min(5, Files.length)).map(file => ({
                            resourceType: "file" as const,
                            data: file,
                        }));

            const restoredOpenSnippets = fallbackTabs
                .filter((tab): tab is Extract<OpenTab, { resourceType: "snippet" }> =>
                    tab.resourceType === "snippet"
                )
                .map(tab => tab.data);

            setOpenSnippets(restoredOpenSnippets);

            setOpenTabs(fallbackTabs);

            const firstTab = fallbackTabs[0] ?? null;

            setActiveFileId(
                firstTab
                    ? {
                        resourceType: firstTab.resourceType,
                        id: firstTab.data._id,
                    }
                    : null
            );

            setActiveSnippetId(
                firstTab?.resourceType === "snippet" ? firstTab.data._id : null
            );

            setActiveLanguage(
                firstTab?.resourceType === "snippet"
                    ? firstTab.data.snippetLanguage || ""
                    : ""
            );

            if (!fallbackTabs.length) {
                setUiMode("files");
                setSbMode(prev => prev === "editor" ? "filelist" : prev);
                localStorage.setItem("flashroom-ui-mode", "files");
            }

            // Only mark hydrated on a successful restore — leaving it
            // unmarked on failure (below) lets a later Snippets/Files update
            // retry automatically instead of staying stuck.
            hydratedRoomIdsRef.current.add(roomId);
        } catch {
            // Restoring failed unexpectedly — fall back to Drive mode rather
            // than leaving the editor in a broken empty state. The user can
            // always switch back to Editor via UiModeToggle, which will retry
            // this effect since the room isn't marked hydrated.
            setOpenTabs([]);
            setOpenSnippets([]);
            setActiveFileId(null);
            setActiveSnippetId(null);
            setActiveLanguage("");
            setUiMode("files");
            setSbMode(prev => prev === "editor" ? "filelist" : prev);
            localStorage.setItem("flashroom-ui-mode", "files");
        }
    }, [roomId, Snippets, Files]);

    useEffect(() => {
        if (!roomId) return;

        if (!hydratedRoomIdsRef.current.has(roomId)) {
            return;
        }

        const savedTabs: SavedOpenTab[] = openTabs.map(tab => ({
            resourceType: tab.resourceType,
            id: tab.data._id,
        }));

        writeJSONCookie(openTabsCookieKeyFor(roomId), savedTabs, {
            days: 7,
            path: "/",
            sameSite: "Lax",
        });
    }, [roomId, openTabs]);






    useEffect(() => {
        const id = localStorage.getItem('flashroom_id');
        setClientId(id);
    }, [roomId]);






    // useJoinSnippetRoom(roomId, activeSnippetId?.toString() || undefined);


    const navigate = useNavigate();
    const recoveryInFlightRef = useRef<Promise<void> | null>(null);
    const lastWakeCheckRef = useRef(Date.now());

    type ListSnippetsRes = {
        ok: true;
        snippets: Snippet[];
        nextCursor?: string;
    };



    const loadMembers = useCallback(async () => {
        const { data } = await api.get(`/rooms/${roomId}/listMembers`)
        setMembersList(data.members);
    }, [roomId])


    const loadYourData = useCallback(async () => {
        try {
            if (!roomId || !clientId) return;

            const { data } = await api.get<{ member: RoomMemberDetails }>(
                `/rooms/${roomId}/members/${encodeURIComponent(clientId)}/details`
            );

            setMemberDetails(data.member);
            setYourRole(data.member.role as YourRole);
        } catch (err) {
            setMemberDetails(null);
            setYourRole(null);
        }
    }, [roomId, clientId]);

    useEffect(() => {
        if (!roomId || !clientId) return;

        loadYourData();

        const onVis = () => {
            if (document.visibilityState === "visible") loadYourData();
        };

        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [roomId, clientId, loadYourData]);




    const loadFiles = useCallback(async () => {
        const { data } = await api.get(`/rooms/${roomId}/files/getfiles`);

        setFiles(data.files);

    }, [roomId])

    const addFileById = useCallback(async (id: string) => {
        if (!roomId) return;
        try {
            const { data } = await api.get(`/rooms/${roomId}/files/${id}`);
            if (!data?.ok || !data.file) return;
            setFiles(prev => prev.some(f => f._id === data.file._id) ? prev : [data.file, ...prev]);
        } catch { }
    }, [roomId]);

    useEffect(() => {
        if (!roomId) return;

        loadFiles();

        const onVis = () => document.visibilityState === "visible" && loadFiles();
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [roomId, loadFiles]);


    useEffect(() => {
        if (!roomId) return;

        let t: ReturnType<typeof setTimeout> | null = null;

        const onRefresh = () => {
            if (t) clearTimeout(t);
            t = setTimeout(() => {
                loadFiles();
                reloadStorage();
            }, 50);
        };


        socket.on("files:refresh", onRefresh);

        return () => {
            if (t) clearTimeout(t);
            socket.off("files:refresh", onRefresh);
        };
    }, [roomId, loadFiles]);

    useEffect(() => {
        if (!roomId) return;

        const onFileCreated = (p: { _id?: string; folderParent?: string | null; storageDelta?: number }) => {
            if (!p?._id) return;
            addFileById(p._id);
            applyStorageDelta(p.storageDelta);
            if (p.folderParent) {
                setFolders(prev => addIdToFolderArray(prev, p.folderParent as string, "fileRefs", p._id as string));
            }
        };

        socket.on("file:created", onFileCreated);

        return () => {
            socket.off("file:created", onFileCreated);
        };
    }, [roomId, addFileById, applyStorageDelta]);

    useEffect(() => {
        if (!roomId) return;

        const onFileDeleted = (p: { _id?: string; storageDelta?: number }) => {
            if (!p?._id) return;
            removeFileById(p._id);
            applyStorageDelta(p.storageDelta);
        };

        const onFileMoved = (p: { _id?: string; folderParent?: string | null }) => {
            if (!p?._id) return;
            patchFileById(p._id, { folderParent: p.folderParent ?? null });
            setFolders(prev => {
                const stripped = removeIdFromFolderArrays(prev, "fileRefs", p._id as string);
                return p.folderParent
                    ? addIdToFolderArray(stripped, p.folderParent, "fileRefs", p._id as string)
                    : stripped;
            });
        };

        socket.on("file:deleted", onFileDeleted);
        socket.on("file:moved", onFileMoved);

        return () => {
            socket.off("file:deleted", onFileDeleted);
            socket.off("file:moved", onFileMoved);
        };
    }, [roomId, removeFileById, applyStorageDelta, patchFileById]);



    useEffect(() => {
        if (!roomId) return;

        loadMembers();

        const onVis = () => document.visibilityState === "visible" && loadMembers();
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [roomId, loadMembers]);


    useEffect(() => {
        if (!roomId) return;

        let t: ReturnType<typeof setTimeout> | null = null;

        const onRefresh = () => {
            if (t) clearTimeout(t);
            t = setTimeout(() => {
                loadMembers();
                loadYourData();
            }, 50);
        };

        socket.on("members:refresh", onRefresh);

        return () => {
            if (t) clearTimeout(t);
            socket.off("members:refresh", onRefresh);
        };
    }, [roomId, loadMembers, loadYourData]);

    useEffect(() => {
        if (!roomId) return;

        const onPresence = (p: { clientId?: string; isActive?: boolean }) => {
            if (!p?.clientId || typeof p.isActive !== "boolean") return;
            patchMemberById(p.clientId, { isActive: p.isActive });
        };

        const onPresenceBatch = (p: { clientIds?: string[]; isActive?: boolean }) => {
            if (!Array.isArray(p?.clientIds) || typeof p.isActive !== "boolean") return;
            for (const clientId of p.clientIds) patchMemberById(clientId, { isActive: p.isActive });
        };

        const onMemberUpdated = (p: { clientId?: string; role?: string; canCreate?: boolean; name?: string }) => {
            if (!p?.clientId) return;
            const { clientId: id, ...fields } = p;
            patchMemberById(id, fields);
        };

        socket.on("member:presence", onPresence);
        socket.on("member:presence-batch", onPresenceBatch);
        socket.on("member:updated", onMemberUpdated);

        return () => {
            socket.off("member:presence", onPresence);
            socket.off("member:presence-batch", onPresenceBatch);
            socket.off("member:updated", onMemberUpdated);
        };
    }, [roomId, patchMemberById]);

    useEffect(() => {
        if (!roomId || !clientId) return;

        const sendHeartbeat = () => {
            if (!socket.connected) return;
            socket.emit("presence:heartbeat", { roomId, clientId });
        };

        sendHeartbeat();
        const heartbeatId = window.setInterval(sendHeartbeat, PRESENCE_HEARTBEAT_MS);

        socket.on("connect", sendHeartbeat);

        return () => {
            window.clearInterval(heartbeatId);
            socket.off("connect", sendHeartbeat);
        };
    }, [roomId, clientId]);

    useEffect(() => {
        const handleLeave = () => {
            navigator.sendBeacon(`${API}/api/rooms/${roomId}/members/${clientId}/exitleave`);
        };

        window.addEventListener("beforeunload", handleLeave);
        window.addEventListener("pagehide", handleLeave);

        return () => {
            window.removeEventListener("beforeunload", handleLeave);
            window.removeEventListener("pagehide", handleLeave);
        };
    }, [roomId, clientId]);

    //first one only sent on page close this should change member to unactive on navigation

    useEffect(() => {
        return () => {
            if (!roomId || !clientId) return;

            navigator.sendBeacon(
                `${API}/api/rooms/${roomId}/members/${clientId}/exitleave`
            );
        };
    }, [roomId, clientId]);


    //since send beacon also fires on refresh this will set user to active again

    const refreshRejoinAttemptedRef = useRef(false);



    useEffect(() => {
        if (!roomId || !clientId) return;
        if (refreshRejoinAttemptedRef.current) return;

        const runRefreshRejoin = async () => {
            refreshRejoinAttemptedRef.current = true;

            try {
                await api.patch(`/rooms/${roomId}/members/${clientId}/rejoin`);

                await loadMembers();
                await loadYourData();
            } catch (err) {
                refreshRejoinAttemptedRef.current = false;
            }
        };

        runRefreshRejoin();
    }, [roomId, clientId, loadMembers, loadYourData]);



    const loadSnippets = useCallback(async () => {
        if (!roomId) return;
        const { data } = await api.get<ListSnippetsRes>(`/rooms/${roomId}/snippets`);
        setSnippets(data.snippets);


    }, [roomId]);

    const addSnippetById = useCallback(async (id: string) => {
        if (!roomId) return;
        try {
            const { data } = await api.get(`/rooms/${roomId}/snippets/${id}`);
            if (!data?.ok || !data.snippet) return;
            setSnippets(prev => prev.some(s => s._id === data.snippet._id) ? prev : [data.snippet, ...prev]);
        } catch { }
    }, [roomId]);


    const reloadStorage = useCallback(async () => {
        if (!roomId) return;
        const { data } = await api.get(`/rooms/${roomId}/getStorage`);
        setRoomStorage(data.storage);
    }, [roomId]);

    useEffect(() => {
        if (!roomId) return;

        reloadStorage();

        const onVis = () => document.visibilityState === "visible" && reloadStorage();
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [roomId, reloadStorage]);


    useEffect(() => {
        if (!roomId) return;

        loadSnippets();

        const onVis = () => document.visibilityState === "visible" && loadSnippets();
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [roomId, loadSnippets]);

    useEffect(() => {
        if (!roomId) return;


        let t: ReturnType<typeof setTimeout> | null = null;
        const onRefresh = () => {
            if (t) clearTimeout(t);
            t = setTimeout(() => {
                loadSnippets();
                reloadStorage();
            }, 50);
        };

        socket.on("snippets:refresh", onRefresh);

        return () => {
            if (t) clearTimeout(t);
            socket.off("snippets:refresh", onRefresh);
        };
    }, [roomId, loadSnippets]);

    useEffect(() => {
        if (!roomId) return;

        const onSnippetUpdated = (p: {
            _id?: string;
            snippetName?: string;
            snipPermissions?: string;
            sizeBytes?: number;
            storageDelta?: number;
            updatedAt?: string;
        }) => {
            if (!p?._id) return;
            const { _id, storageDelta, updatedAt, ...fields } = p;
            patchSnippetById(_id, fields, updatedAt);
            applyStorageDelta(storageDelta);
        };

        socket.on("snippet:updated", onSnippetUpdated);

        return () => {
            socket.off("snippet:updated", onSnippetUpdated);
        };
    }, [roomId, patchSnippetById, applyStorageDelta]);

    // Lightweight counterpart to snippet:updated for pure text-content saves,
    // which skip the full snippets:refresh (see SnippetContainer's autosave
    // path) — this just moves the storage total without refetching anything.
    // The saving client applies this same delta locally from its own PATCH
    // response instead, so it's excluded here via senderClientId to avoid
    // double-counting.
    useEffect(() => {
        if (!roomId) return;

        const onStorageDelta = (p: { senderClientId?: string; storageDelta?: number }) => {
            if (typeof p?.storageDelta !== "number") return;
            if (p.senderClientId && p.senderClientId === clientId) return;
            applyStorageDelta(p.storageDelta);
        };

        socket.on("snippet:storage-delta", onStorageDelta);

        return () => {
            socket.off("snippet:storage-delta", onStorageDelta);
        };
    }, [roomId, clientId, applyStorageDelta]);

    useEffect(() => {
        if (!roomId) return;

        const onSnippetCreated = (p: { _id?: string; folderParent?: string | null; storageDelta?: number }) => {
            if (!p?._id) return;
            addSnippetById(p._id);
            applyStorageDelta(p.storageDelta);
            if (p.folderParent) {
                setFolders(prev => addIdToFolderArray(prev, p.folderParent as string, "snippets", p._id as string));
            }
        };

        socket.on("snippet:created", onSnippetCreated);

        return () => {
            socket.off("snippet:created", onSnippetCreated);
        };
    }, [roomId, addSnippetById, applyStorageDelta]);

    useEffect(() => {
        if (!roomId) return;

        const onSnippetDeleted = (p: { _id?: string; storageDelta?: number }) => {
            if (!p?._id) return;
            removeSnippetById(p._id);
            applyStorageDelta(p.storageDelta);
        };

        const onSnippetMoved = (p: { _id?: string; folderParent?: string | null }) => {
            if (!p?._id) return;
            patchSnippetById(p._id, { folderParent: p.folderParent ?? null });
            setFolders(prev => {
                const stripped = removeIdFromFolderArrays(prev, "snippets", p._id as string);
                return p.folderParent
                    ? addIdToFolderArray(stripped, p.folderParent, "snippets", p._id as string)
                    : stripped;
            });
        };

        socket.on("snippet:deleted", onSnippetDeleted);
        socket.on("snippet:moved", onSnippetMoved);

        return () => {
            socket.off("snippet:deleted", onSnippetDeleted);
            socket.off("snippet:moved", onSnippetMoved);
        };
    }, [roomId, removeSnippetById, applyStorageDelta, patchSnippetById]);

    const loadFolders = useCallback(async () => {
        try {
            const { data: folders } = await api.get(`/rooms/${roomId}/folder`);
            setFolders(folders.folders)
        } catch (error) {

        }
    }, [roomId])


    useEffect(() => {
        setOpenTabs(prev => {
            let changed = false;

            const next = prev
                .map(tab => {
                    if (tab.resourceType !== "snippet") return tab;

                    const updatedSnippet = Snippets.find(
                        snippet => snippet._id === tab.data._id
                    );

                    // Snippet was deleted or no longer exists
                    if (!updatedSnippet) {
                        changed = true;
                        return null;
                    }

                    // Replace stale tab data with newest backend snippet
                    if (updatedSnippet !== tab.data) {
                        changed = true;
                        return {
                            resourceType: "snippet" as const,
                            data: updatedSnippet,
                        };
                    }

                    return tab;
                })
                .filter((tab): tab is OpenTab => tab !== null);

            return changed ? next : prev;
        });
    }, [Snippets]);



    useEffect(() => {
        setOpenTabs(prev => {
            let changed = false;

            const next = prev
                .map(tab => {
                    if (tab.resourceType !== "file") return tab;

                    const updatedFile = Files.find(
                        file => file._id === tab.data._id
                    );

                    // File was deleted or no longer exists
                    if (!updatedFile) {
                        changed = true;
                        return null;
                    }

                    // Replace stale tab data with newest backend file
                    if (updatedFile !== tab.data) {
                        changed = true;
                        return {
                            resourceType: "file" as const,
                            data: updatedFile,
                        };
                    }

                    return tab;
                })
                .filter((tab): tab is OpenTab => tab !== null);

            return changed ? next : prev;
        });
    }, [Files]);


    useEffect(() => {
        if (!roomId) return;

        loadFolders();

        const onVis = () => document.visibilityState === "visible" && loadFolders();
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [roomId, loadFolders]);

    useEffect(() => {
        if (!roomId) return;


        let t: ReturnType<typeof setTimeout> | null = null;
        const onRefresh = () => {
            if (t) clearTimeout(t);
            t = setTimeout(() => {
                loadFolders();
            }, 50);
        };


        socket.on("folders:refresh", onRefresh);

        return () => {
            if (t) clearTimeout(t);
            socket.off("folders:refresh", onRefresh);
        };
    }, [roomId, loadFolders]);

    useEffect(() => {
        if (!roomId) return;

        const onFolderRenamed = (p: { _id?: string; fname?: string }) => {
            if (!p?._id || typeof p.fname !== "string") return;
            patchFolderById(p._id, { fname: p.fname });
        };

        const onFolderPermissionUpdated = (p: {
            _id?: string;
            folderPermission?: "restricted" | "open";
            inheritPermission?: boolean;
        }) => {
            if (!p?._id) return;
            const { _id, ...fields } = p;
            patchFolderById(_id, fields);
        };

        socket.on("folder:renamed", onFolderRenamed);
        socket.on("folder:permission-updated", onFolderPermissionUpdated);

        return () => {
            socket.off("folder:renamed", onFolderRenamed);
            socket.off("folder:permission-updated", onFolderPermissionUpdated);
        };
    }, [roomId, patchFolderById]);

    useEffect(() => {
        if (!roomId) return;

        const onFolderCreated = (p: { node?: Folder; parentId?: string | null }) => {
            if (!p?.node?._id) return;
            setFolders(prev => insertFolderNode(prev, p.parentId ?? null, p.node as Folder));
        };

        socket.on("folder:created", onFolderCreated);

        return () => {
            socket.off("folder:created", onFolderCreated);
        };
    }, [roomId]);

    useEffect(() => {
        if (!roomId) return;

        const onFolderDeleted = (p: {
            folderIds?: string[];
            snippetIds?: string[];
            fileIds?: string[];
            storageDelta?: number;
        }) => {
            if (!Array.isArray(p?.folderIds) || !p.folderIds.length) return;

            const folderIdSet = new Set(p.folderIds);
            setFolders(prev => removeFolderIds(prev, folderIdSet));

            if (p.snippetIds?.length) {
                const snippetIdSet = new Set(p.snippetIds);
                setSnippets(prev => prev.filter(s => !snippetIdSet.has(s._id)));
            }

            if (p.fileIds?.length) {
                const fileIdSet = new Set(p.fileIds);
                setFiles(prev => prev.filter(f => !fileIdSet.has(f._id)));
            }

            applyStorageDelta(p.storageDelta);
        };

        const onFolderMoved = (p: {
            _id?: string;
            folderParent?: string | null;
            inheritPermission?: boolean;
            folderPermission?: "restricted" | "open";
        }) => {
            if (!p?._id) return;

            setFolders(prev => {
                const { tree, node } = extractFolderNode(prev, p._id as string);
                if (!node) return prev;

                const patchedNode: Folder = {
                    ...node,
                    folderParent: p.folderParent ?? null,
                    ...(p.inheritPermission !== undefined ? { inheritPermission: p.inheritPermission } : {}),
                    ...(p.folderPermission !== undefined ? { folderPermission: p.folderPermission } : {}),
                };

                return insertFolderNode(tree, p.folderParent ?? null, patchedNode);
            });
        };

        socket.on("folder:deleted", onFolderDeleted);
        socket.on("folder:moved", onFolderMoved);

        return () => {
            socket.off("folder:deleted", onFolderDeleted);
            socket.off("folder:moved", onFolderMoved);
        };
    }, [roomId, applyStorageDelta]);


    type GetRoomResponse =
        | {
            ok: true;
            roomDetails: roomDetails;
        }
        | {
            ok: false;
            error: string;
        };

    const loadRoomDetails = useCallback(async () => {
        try {
            const { data } = await api.get<GetRoomResponse>(`/rooms/${roomId}`);

            if (data.ok) {
                setRoomDetails(data.roomDetails);
            }
        } catch (error: any) {
            const serverData = error?.response?.data as GetRoomResponse | undefined;

            if (serverData && !serverData.ok && serverData.error === "room not found") {
                navigate("/deleted");
                return;
            }

        }
    }, [roomId, navigate]);

    const getFreshAccountAccessToken = useCallback(async () => {
        if (!auth.user?.access_token && !auth.user?.refresh_token) return null;

        if (
            auth.isAuthenticated &&
            auth.user?.access_token &&
            !shouldRefreshAuthUser(auth.user)
        ) {
            return auth.user.access_token;
        }

        if (auth.activeNavigator === "signinSilent") {
            return auth.user?.access_token ?? null;
        }

        if (!auth.user?.refresh_token) {
            return auth.user?.access_token ?? null;
        }

        try {
            const restoredUser = await auth.signinSilent();
            return restoredUser?.access_token ?? auth.user?.access_token ?? null;
        } catch (err) {
            return auth.user?.access_token ?? null;
        }
    }, [
        auth,
        auth.activeNavigator,
        auth.isAuthenticated,
        auth.user?.access_token,
        auth.user?.expires_at,
        auth.user?.expired,
        auth.user?.refresh_token,
    ]);

    const recoverRoomSession = useCallback(async () => {
        if (!roomId) return;
        if (recoveryInFlightRef.current) return recoveryInFlightRef.current;

        const run = (async () => {
            const accountAccessToken = await getFreshAccountAccessToken();
            const rejoinData = accountAccessToken
                ? (
                    await api.patch(
                        `/rooms/account/${roomId}/rejoin`,
                        undefined,
                        { headers: { Authorization: `Bearer ${accountAccessToken}` } }
                    )
                ).data
                : (
                    await (async () => {
                        const currentClientId = clientId ?? getOrCreateClientId();
                        return axios.patch(`${API}/api/rooms/${roomId}/members/${currentClientId}/rejoin`);
                    })()
                ).data;

            if (rejoinData?.token) {
                localStorage.setItem("flashroom_roomToken", rejoinData.token);
            }

            const currentClientId = localStorage.getItem("flashroom_id");
            if (currentClientId && currentClientId !== clientId) {
                setClientId(currentClientId);
            }

            socket.io.opts.reconnection = true;
            if (!socket.connected) socket.connect();
            socket.emit("room:join", { roomId });

            await Promise.allSettled([
                loadRoomDetails(),
                loadMembers(),
                loadYourData(),
                loadFiles(),
                loadSnippets(),
                loadFolders(),
                reloadStorage(),
            ]);
        })();

        recoveryInFlightRef.current = run;

        try {
            await run;
        } finally {
            recoveryInFlightRef.current = null;
        }
    }, [
        roomId,
        clientId,
        getFreshAccountAccessToken,
        loadRoomDetails,
        loadMembers,
        loadYourData,
        loadFiles,
        loadSnippets,
        loadFolders,
        reloadStorage,
    ]);

    useEffect(() => {
        if (!roomId) return;

        const maybeRecover = (force = false) => {
            const now = Date.now();
            const elapsed = now - lastWakeCheckRef.current;
            lastWakeCheckRef.current = now;

            if (
                force ||
                elapsed >= WAKE_RECOVERY_AFTER_MS ||
                shouldRefreshAuthUser(auth.user)
            ) {
                void recoverRoomSession();
            }
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") maybeRecover();
        };

        const onFocus = () => maybeRecover();
        const onOnline = () => maybeRecover(true);

        document.addEventListener("visibilitychange", onVisibilityChange);
        window.addEventListener("focus", onFocus);
        window.addEventListener("online", onOnline);

        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange);
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("online", onOnline);
        };
    }, [
        roomId,
        auth.user?.access_token,
        auth.user?.expires_at,
        auth.user?.expired,
        recoverRoomSession,
    ]);

    useEffect(() => {
        if (!roomId) return;

        const interceptorId = api.interceptors.response.use(
            response => response,
            async error => {
                const code = error?.response?.data?.code;
                const config = error?.config;
                const requestUrl = String(config?.url ?? "");
                const isRoomRequest = requestUrl.includes(`/rooms/${roomId}`);

                if (
                    config &&
                    code === "ROOM_TOKEN_EXPIRED" &&
                    isRoomRequest &&
                    !config._flashroomRoomRetry
                ) {
                    config._flashroomRoomRetry = true;
                    await recoverRoomSession();
                    return api.request(config);
                }

                return Promise.reject(error);
            }
        );

        return () => {
            api.interceptors.response.eject(interceptorId);
        };
    }, [roomId, recoverRoomSession]);

    useEffect(() => {
        const reRoute = () => {
            navigate("/deleted");
        };

        socket.on("room:deleted", reRoute);

        return () => {
            socket.off("room:deleted", reRoute);
        };
    }, [navigate]);

    useEffect(() => {
        if (!roomId) return;

        // initial load once we know roomId
        loadRoomDetails();

        let t: ReturnType<typeof setTimeout> | null = null;

        const onRefresh = () => {
            if (t) clearTimeout(t);
            t = setTimeout(() => loadRoomDetails(), 50);
        };

        socket.on("room:refresh", onRefresh);

        return () => {
            if (t) clearTimeout(t);
            socket.off("room:refresh", onRefresh);
        };
    }, [roomId, loadRoomDetails]);

    useEffect(() => {
        if (!roomId) return;

        const onSettingsUpdated = (p: Partial<roomDetails>) => {
            if (!p || typeof p !== "object") return;
            setRoomDetails(prev => (prev ? { ...prev, ...p } : prev));
        };

        socket.on("room:settings-updated", onSettingsUpdated);

        return () => {
            socket.off("room:settings-updated", onSettingsUpdated);
        };
    }, [roomId]);

    useEffect(() => {
        if (!limitPopup) return;

        const t = setTimeout(() => {
            setLimitPopup(false);
            setLimitMessage(null);
        }, 2000);

        return () => clearTimeout(t);
    }, [limitPopup, setLimitPopup, setLimitMessage]);

    useEffect(() => {
        if (!roomDetails) return;

        const flashroomId = localStorage.getItem("flashroom_id");
        if (!flashroomId) return; // nothing to compare if we don't know who we are

        const bannedClients = roomDetails.bannedClients ?? [];

        const isBanned = bannedClients.includes(flashroomId);

        if (isBanned) {
            navigate("/kicked");
        }
    }, [roomDetails, navigate]);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', ctheme);
    }, [ctheme])

    const value: WorkspaceCtx = {
        roomDetails,
        roomSettings,
        setRoomDetails,
        activeSnippetId,
        setActiveSnippetId,
        activeMembers,
        memberDetails,
        yourRole,
        setYourRole,
        setMemberDetails,
        roomStorage,
        setRoomStorage,
        activeSnippet,
        ctheme,
        prompt,
        activeTerminal,
        clientId,
        setClientId,
        limitPopup,
        setLimitPopup,
        limitMessage,
        setLimitMessage,
        sideBarOpen,
        openSnippets,
        newSnipPopup,
        pendingOpenId,
        setPendingOpenId,
        uiMode,
        setUiMode,
        sbMode,
        setSbMode,
        setNewSnipPopup: (b: boolean) => setNewSnipPopup(b),
        addNewPopup,
        setAddNewPopup: (b: boolean) => setAddNewPopup(b),
        globalNewFilePopup,
        setGlobalNewFilePopup: (b: boolean) => setGlobalNewFilePopup(b),
        globalNewSnipPopup,
        setGlobalNewSnipPopup: (b: boolean) => setGlobalNewSnipPopup(b),
        globalNewFolderPopup,
        setGlobalNewFolderPopup: (b: boolean) => setGlobalNewFolderPopup(b),
        setOpenSnippets: setOpenSnippets,
        toggleSideBarOpen: (b: boolean) => toggleSideBarOpen(b),
        setcTheme: (t: string) => setcTheme(t),
        setPrompt: (b: boolean) => setPrompt(b),
        setActiveTerminal: (b: boolean) => setActiveTerminal(b),
        Snippets,
        membersList,
        Files,
        Folders,
        setFolders,
        setFiles,
        setMembersList: setMembersList,
        setSnippets: setSnippets,
        openTabs,
        setOpenTabs,
        activeFile,
        activeFileId,
        setActiveFileId,
        downloadFileErrorPopup,
        setDownloadFileErrorPopup: (b: boolean) => setDownloadFileErrorPopup(b),
        hydratedRoomIdsRef,
        roomSettingsInfoPopup,
        setRoomSettingsInfoPopup: (b: boolean) => setRoomSettingsInfoPopup(b),
        qrPopup,
        setQrPopup: (b: boolean) => setQrPopup(b),
    }

    const codeRunnerValue: CodeRunnerCtx = {
        activeCode,
        setActiveCode: (c: string) => setActiveCode(c),
        activeLanguage,
        setActiveLanguage: (l: string) => setActiveLanguage(l),
        activeWebPreview,
        setActiveWebPreview: (b: boolean) => setActiveWebPreview(b),
        previewDoc,
        setPreviewDoc: (p: string) => setPreviewDoc(p),
        outputRef,
        termWriteRef,
        termReadLineRef,
        jsRunRef,
    }


    return (
        <WorkspaceContext.Provider value={value}>
            <CodeRunnerContext.Provider value={codeRunnerValue}>
                <WorkspacePage />
            </CodeRunnerContext.Provider>
        </WorkspaceContext.Provider>
    )
}

export default WorkspaceProvider
